/**
 * Gate-value freshness signal shared by the analytics provider and
 * `useFeatureGate`. `markProviderReady` (index.ts) only fires once, after the
 * initial Statsig fetch, but gate values change again whenever the user is
 * identified after sign-in. Without this store a gated page that rendered
 * during the anonymous window kept its stale `false` until a refresh.
 *
 * - `pending` counts in-flight identity updates; consumers treat >0 as
 *   "still loading" so they never commit to the anonymous evaluation.
 * - `version` bumps when an update settles so subscribers re-read gates.
 */
let version = 0;
let pending = 0;
const listeners: Set<() => void> = new Set();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Call before a provider identity update starts. */
export function beginGateValuesUpdate(): void {
  pending += 1;
  notify();
}

/** Call once that update settles (success or failure). */
export function endGateValuesUpdate(): void {
  pending = Math.max(0, pending - 1);
  version += 1;
  notify();
}

export function subscribeGateValues(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function getGateValuesVersion(): number {
  return version;
}

export function getGateValuesPending(): boolean {
  return pending > 0;
}
